import { Container, ContainerChild, Graphics, Text } from "pixi.js";
import { getDefaultStore } from "jotai";

import type {
  IStudioWidget,
  IStudioWidgetLiveCaptions,
} from "../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Widgets.interface";
import { captionTextAtom } from "../../../captions/captionAtoms";

/**
 * Pixi text scale factor, matching the other text widgets (Crawl / Chat):
 * the config stores design-px font sizes against the 1920-wide canvas.
 */
const FONT_SCALE = 2.01;

interface WidgetState {
  destination: { x: number; y: number; width: number; height: number };
  options: IStudioWidgetLiveCaptions["options"];
  bg?: Graphics;
  mask: Graphics;
  text: Text;
  /** Jotai subscription teardown for `captionTextAtom`. */
  unsubscribe: () => void;
}

/**
 * Pixi renderer for the Live Captions widget. Subscribes to the single
 * global `captionTextAtom` (written by `LiveCaptionsService` from the ASR
 * sidecar) and paints the current caption block within the widget bounds.
 *
 * It does NOT start/stop the ASR itself — that is driven by the widget's
 * `active` flag from `WidgetsFactory` (see `reconcileActive`), keeping a
 * single program-wide ASR stream regardless of how many caption widgets
 * exist.
 */
export class LiveCaptionsWidgetRenderer {
  private widgets = new Map<string, WidgetState>();
  private store = getDefaultStore();

  render(
    widget: IStudioWidget,
    container: Container,
    destination: { x: number; y: number; width: number; height: number },
  ): void {
    const w = widget as IStudioWidgetLiveCaptions;
    const o = w.options;

    // Tear down any prior state for this widget id (re-renders fire when
    // bounds / options change in the property panel).
    this.cleanupWidget(widget.id);
    container.removeChildren();

    // Optional background plate.
    let bg: Graphics | undefined;
    if (o.background) {
      bg = new Graphics();
      bg.roundRect(0, 0, destination.width, destination.height, 8).fill({
        color: 0x000000,
        alpha: 0.55,
      });
      container.addChild(bg);
    }

    // Clip the text to the widget bounds.
    const mask = new Graphics();
    mask.rect(0, 0, destination.width, destination.height).fill(0xffffff);
    container.addChild(mask);
    container.mask = mask;

    const fontFamily = o.fontFamily ?? "Inter, system-ui, sans-serif";
    const fontSize = (o.fontSize ?? 28) * FONT_SCALE;
    const color = o.color ?? "#ffffff";
    const padX = Math.max(8, destination.width * 0.03);

    const text = new Text({
      text: this.store.get(captionTextAtom),
      style: {
        fontFamily,
        fontSize,
        fill: color,
        align: "center",
        wordWrap: true,
        wordWrapWidth: Math.max(40, destination.width - padX * 2),
        // Drop shadow for legibility over arbitrary scene content (esp.
        // when no background plate is drawn).
        dropShadow: {
          color: 0x000000,
          alpha: 0.8,
          blur: 4,
          distance: 0,
        },
      },
    });
    text.anchor.set(0.5, 1);
    container.addChild(text as unknown as ContainerChild);

    const layout = () => {
      text.x = destination.width / 2;
      // Bottom-anchored within the bounds (caption convention).
      text.y = destination.height;
    };
    layout();

    const applyText = (value: string) => {
      if (text.destroyed) return;
      text.text = value;
      layout();
    };

    // Subscribe to the live caption text.
    const unsubscribe = this.store.sub(captionTextAtom, () => {
      applyText(this.store.get(captionTextAtom));
    });

    this.widgets.set(widget.id, {
      destination,
      options: o,
      bg,
      mask,
      text,
      unsubscribe,
    });
  }

  private cleanupWidget(widgetId: string): void {
    const existing = this.widgets.get(widgetId);
    if (!existing) return;
    try {
      existing.unsubscribe();
    } catch {
      /* best-effort */
    }
    this.widgets.delete(widgetId);
  }

  destroy(): void {
    for (const id of [...this.widgets.keys()]) {
      this.cleanupWidget(id);
    }
  }
}

export default LiveCaptionsWidgetRenderer;
